import useStorefrontCurrencies from '../hooks/useStorefrontCurrencies';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Layout, Button, Input, Badge, Avatar, Drawer, Empty, message, Tooltip } from 'antd';
import {
  ShoppingOutlined, SearchOutlined, BellOutlined, MenuOutlined,
  HomeOutlined, LogoutOutlined, QuestionCircleOutlined,
  WalletOutlined, CloseOutlined, ArrowLeftOutlined, UserOutlined, LoginOutlined,
  GiftOutlined,
  DollarOutlined, RightOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance, { isRequestCanceled } from '../api/axiosConfig';
import { getHomeData } from '../utils/homeBootstrap';
import { useI18n } from '../i18n/I18nProvider';
import { safeGetItem, safeJsonGet, safeJsonSet, safeRemoveItem } from '../utils/safeStorage';
import useScrollHeader from '../hooks/useScrollHeader';
import DeferredNavbarDialog, { cacheNavbarDialog } from './DeferredNavbarDialog';
import {
  readPaymentCurrency,
  writePaymentCurrency
} from '../utils/paymentCurrency';

const { Header } = Layout;
const loadPreferencesDialog = cacheNavbarDialog(() => import(/* webpackChunkName: "navbar-preferences" */ './NavbarPreferencesDialog'));
const loadTelegramDialog = cacheNavbarDialog(() => import(/* webpackChunkName: "navbar-telegram" */ './NavbarTelegramDialog'));
const MOBILE_HEADER_QUERY = '(max-width: 850px)';

const buildAvatarSrc = (userInfo) =>
  userInfo?.avatar_url ||
  userInfo?.profile_image ||
  userInfo?.google_photo_url ||
  `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(userInfo?.username || 'User')}`;

const getGameSearchText = (game = {}) =>
  [
    game.game_name,
    game.title,
    game.name,
    game.game_code,
    game.category_name,
    game.message,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

const getGamesPayload = (payload = {}) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.data?.games)) return payload.data.games;
  if (Array.isArray(payload.games)) return payload.games;
  return [];
};

const getSearchImageSrc = (game = {}) =>
  game.home_logo || game.game_logo || '/image/jla-logo.webp';

const Navbar = () => {
  const { currency: paymentCurrency, currencies: availableCurrencies } = useStorefrontCurrencies();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage, t } = useI18n();

  // UI States
  const [scrolled, setScrolled] = useState(false);
  const [mobileHeader, setMobileHeader] = useState(() =>
    typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_HEADER_QUERY).matches
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [showDesktopSearch, setShowDesktopSearch] = useState(false);
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);
  const [draftLanguage, setDraftLanguage] = useState(language);
  const [draftPaymentCurrency, setDraftPaymentCurrency] = useState(() => readPaymentCurrency());

  // Search States
  const [searchValue, setSearchValue] = useState('');
  const [allGames, setAllGames] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);

  // Auth State
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  // Telegram Modal States
  const [isTelegramModalOpen, setIsTelegramModalOpen] = useState(false);
  const [generatedOtp, setGeneratedOtp] = useState(null);
  const [botUsername, setBotUsername] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const pollingTimeout = useRef(null);
  const pollingAttempt = useRef(0);
  const desktopSearchRef = useRef(null);
  const headerRef = useRef(null);
  // All compact/mobile pages share this behavior; preserve the existing desktop
  // game-1 behavior without changing headers on other desktop pages.
  const autoHideHeader = mobileHeader || /^\/game\/1\/?$/.test(location.pathname);
  const headerHidden = useScrollHeader({
    enabled: autoHideHeader,
    pinned: mobileOpen || showMobileSearch || showDesktopSearch || isPreferencesModalOpen || isTelegramModalOpen,
    headerRef,
    resetKey: location.pathname,
  });

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const viewport = window.matchMedia(MOBILE_HEADER_QUERY);
    const updateViewport = () => setMobileHeader(viewport.matches);
    updateViewport();
    if (viewport.addEventListener) {
      viewport.addEventListener('change', updateViewport);
      return () => viewport.removeEventListener('change', updateViewport);
    }
    // Older mobile Safari uses the legacy MediaQueryList listener API.
    viewport.addListener?.(updateViewport);
    return () => viewport.removeListener?.(updateViewport);
  }, []);

  // Ref for OTP text selection
  const otpTextRef = useRef(null);

  const fontStyle = { fontFamily: "'Noto Sans Khmer', 'Poppins', sans-serif" };
  const telegramIconSrc = '/image/Telegram_logo.svg.png';
  const theme = {
    primary: 'var(--color-primary)',
    primaryDeep: 'var(--color-primary-deep)',
    primarySoft: 'var(--color-primary-soft)',
    success: 'var(--color-success)',
    successSoft: 'var(--color-success-soft)',
    successBorder: 'rgb(var(--color-success-rgb) / 0.35)',
    background: 'var(--color-background)',
    backgroundSoft: 'var(--color-background-soft)',
    foreground: 'var(--color-foreground)',
    muted: 'var(--color-muted)',
    border: 'var(--color-border)',
    borderSoft: 'var(--color-border-soft)',
  };

  // ✅ 1. CHECK LOGIN & FETCH BASIC DATA
  useEffect(() => {
    const controller = new AbortController();

    const checkAuthAndFetchData = async () => {
      const token = safeGetItem('accessToken');

      if (token) {
        setIsLoggedIn(true);

        const cachedUser = safeJsonGet('user', null);
        if (cachedUser) {
          setUserInfo(cachedUser);
          setIsConnected(!!cachedUser.telegram_chat_id);
        }

        try {
          const res = await axiosInstance.get('/auth/me', { signal: controller.signal });
          if (res.data.status === 'success') {
            const freshData = res.data.data;
            setUserInfo(freshData);
            setIsConnected(!!freshData.telegram_chat_id);
            safeJsonSet('user', freshData);
          }
        } catch (error) {
          if (isRequestCanceled(error)) return;
          console.error("Failed to refresh user data", error);
        }
      } else {
        setIsLoggedIn(false);
        setUserInfo(null);
        setIsConnected(false);
      }
    };
    checkAuthAndFetchData();

    return () => controller.abort();
  }, [location.pathname]);

  useEffect(() => {
    const syncUserFromStorage = () => {
      const token = safeGetItem('accessToken');
      const cachedUser = safeJsonGet('user', null);

      if (!token || !cachedUser) {
        setIsLoggedIn(false);
        setUserInfo(null);
        setIsConnected(false);
        return;
      }

      setIsLoggedIn(true);
      setUserInfo(cachedUser);
      setIsConnected(!!cachedUser.telegram_chat_id);
    };

    window.addEventListener('storage', syncUserFromStorage);
    window.addEventListener('user-updated', syncUserFromStorage);

    return () => {
      window.removeEventListener('storage', syncUserFromStorage);
      window.removeEventListener('user-updated', syncUserFromStorage);
    };
  }, []);



  // ✅ 2. Fetch Games
  useEffect(() => {
    const controller = new AbortController();

    const fetchGames = async () => {
      try {
        const res = await getHomeData({ signal: controller.signal });
        if (controller.signal.aborted) return;
        setAllGames(getGamesPayload(res.data));
      } catch (err) {
        if (isRequestCanceled(err)) return;
        console.error("Search API Error:", err);
      }
    };
    fetchGames();

    return () => controller.abort();
  }, []);

  // ✅ 3. Scroll Effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const clearTelegramPolling = useCallback(() => {
    if (pollingTimeout.current) {
      clearTimeout(pollingTimeout.current);
      pollingTimeout.current = null;
    }
    pollingAttempt.current = 0;
  }, []);

  // ✅ 4. Telegram Status Checker (Polling)
  const fetchTelegramStatus = useCallback(async () => {
    if (!isLoggedIn) return false;
    try {
      const res = await axiosInstance.get('/telegram/status');
      if (res.data.status === 'success') {
        const connected = res.data.connected;
        setIsConnected(connected);

        if (connected && userInfo && !userInfo.telegram_chat_id) {
            const meRes = await axiosInstance.get('/auth/me');
            if(meRes.data.status === 'success') {
                setUserInfo(meRes.data.data);
                safeJsonSet('user', meRes.data.data);
            }
        }
        return connected;
      }
    } catch (error) {
      console.error("Telegram status check failed", error);
    }
    return false;
  }, [isLoggedIn, userInfo]);

  useEffect(() => {
    clearTelegramPolling();

    if (isTelegramModalOpen && isLoggedIn) {
      fetchTelegramStatus();

      if (generatedOtp && !isConnected) {
        const scheduleNextPoll = async () => {
          const connected = await fetchTelegramStatus();
          if (connected) {
            clearTelegramPolling();
            return;
          }

          const delayMs = Math.min(3000 * (2 ** pollingAttempt.current), 15000);
          pollingAttempt.current += 1;
          pollingTimeout.current = setTimeout(scheduleNextPoll, delayMs);
        };

        pollingTimeout.current = setTimeout(scheduleNextPoll, 3000);
      }
    }

    return clearTelegramPolling;
  }, [clearTelegramPolling, fetchTelegramStatus, generatedOtp, isConnected, isLoggedIn, isTelegramModalOpen]);

  // ✅ 5. Search Logic
  const getSearchResults = useCallback((value, games = allGames) => {
    const normalizedValue = value.trim().toLowerCase();
    const source = normalizedValue
      ? games.filter((game) => getGameSearchText(game).includes(normalizedValue))
      : games;

    return source.slice(0, 50);
  }, [allGames]);

  const isMobileSearchViewport = useCallback(() =>
    typeof window !== 'undefined' && window.matchMedia(MOBILE_HEADER_QUERY).matches,
  []);

  const openSearchModal = useCallback(() => {
    setFilteredResults(getSearchResults(searchValue));
    if (isMobileSearchViewport()) {
      setShowDesktopSearch(false);
      setShowMobileSearch(true);
      return;
    }

    setShowMobileSearch(false);
    setShowDesktopSearch(true);
  }, [getSearchResults, isMobileSearchViewport, searchValue]);

  const closeSearchModal = useCallback(() => {
    setShowMobileSearch(false);
    setShowDesktopSearch(false);
  }, []);

  useEffect(() => {
    if (showMobileSearch || showDesktopSearch) {
      setFilteredResults(getSearchResults(searchValue));
    }
  }, [getSearchResults, searchValue, showDesktopSearch, showMobileSearch]);

  useEffect(() => {
    if (!showMobileSearch) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousRootOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousRootOverflow;
    };
  }, [showMobileSearch]);

  useEffect(() => {
    if (!showDesktopSearch) return undefined;

    const handlePointerDown = (event) => {
      if (!desktopSearchRef.current?.contains(event.target)) {
        setShowDesktopSearch(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [showDesktopSearch]);

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setFilteredResults(getSearchResults(value));
  };

  const handlePaymentCurrencyChange = useCallback((currency) => {
    writePaymentCurrency(currency);
  }, []);

  const preferenceLabel = `${language === 'km' ? 'KH' : 'EN'} / ${paymentCurrency}`;

  const openPreferencesModal = useCallback(() => {
    setDraftLanguage(language);
    setDraftPaymentCurrency(paymentCurrency);
    setIsPreferencesModalOpen(true);
  }, [language, paymentCurrency]);

  const confirmPreferences = () => {
    setLanguage(draftLanguage);
    handlePaymentCurrencyChange(draftPaymentCurrency);
    setIsPreferencesModalOpen(false);
  };

  const handleSelectGame = (gameId) => {
    setShowMobileSearch(false);
    setShowDesktopSearch(false);
    setSearchValue('');
    navigate(`/game/${gameId}`);
  };

  const onGlobalSearch = () => {
    if (searchValue.trim()) {
      navigate(`/?q=${encodeURIComponent(searchValue)}`);
      setShowMobileSearch(false);
      setShowDesktopSearch(false);
    }
  };

  const handleLogout = () => {
    safeRemoveItem('accessToken');
    safeRemoveItem('user');
    setIsLoggedIn(false);
    setUserInfo(null);
    setMobileOpen(false);
    window.location.href = '/login';
  };

  // --- TELEGRAM LOGIC ---
  const handleGenerateOtp = async () => {
    setActionLoading(true);
    try {
      const res = await axiosInstance.post('/telegram/generate-otp');
      if (res.data.status === 'success') {
        setGeneratedOtp(res.data.otp);
        setBotUsername(res.data.bot_username || 'jlastoreapp_bot');
      } else {
        message.error(t('nav.failedGenerateCode'));
      }
    } catch (error) {
      message.error(error.response?.data?.message || t('nav.generateOtpError'));
    } finally {
      setActionLoading(false);
    }
  };

  const selectOtpText = () => {
    if (!otpTextRef.current) return false;

    const range = document.createRange();
    range.selectNodeContents(otpTextRef.current);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    return true;
  };

  // ✅ ENHANCED COPY FUNCTION
  const handleCopyCode = async () => {
    if (!generatedOtp) return;

    const text = String(generatedOtp);

    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        message.success(t('nav.codeCopied'));
        return;
      } catch (err) {
        console.warn('Clipboard API failed, trying fallback', err);
      }
    }

    if (selectOtpText()) {
      try {
        const successful = document.execCommand('copy');
        window.getSelection().removeAllRanges();
        if (successful) {
          message.success(t('nav.codeCopied'));
          return;
        }
      } catch (err) {
        console.warn('Visible selection copy failed', err);
        window.getSelection().removeAllRanges();
      }
    }

    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.top = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, textArea.value.length);

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        message.success(t('nav.codeCopied'));
      } else {
        message.error(t('nav.copyFailedManual'));
      }
    } catch (err) {
      console.error('All copy methods failed', err);
      message.error(t('nav.copyFailedManual'));
    }
  };

  const handleTestMessage = async () => {
    setActionLoading(true);
    try {
      const res = await axiosInstance.post('/telegram/test-message');
      if (res.data.status === 'success') {
        message.success(t('nav.testSent'));
      } else {
        message.error(res.data.message || t('nav.testSendFailed'));
      }
    } catch (error) {
      message.error(t('nav.testSendFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setActionLoading(true);
    try {
      const res = await axiosInstance.post('/telegram/disconnect');
      if (res.data.status === 'success') {
        message.success(t('nav.telegramDisconnected'));
        setIsConnected(false);
        setGeneratedOtp(null);

        const newUserInfo = { ...userInfo, telegram_chat_id: null };
        setUserInfo(newUserInfo);
        safeJsonSet('user', newUserInfo);
      } else {
        message.error(res.data.message || t('nav.disconnectFailed'));
      }
    } catch (error) {
      message.error(t('nav.disconnectError'));
    } finally {
      setActionLoading(false);
    }
  };

  const finalMenuItems = [
    { title: t('nav.home'), icon: <HomeOutlined />, path: '/' },
    { title: t('nav.orders'), icon: <ShoppingOutlined />, path: '/orders', requireAuth: true },
    { title: t('nav.wallet'), icon: <WalletOutlined />, path: '/wallet', requireAuth: true },
    { title: t('nav.coupons'), icon: <GiftOutlined />, path: '/coupons', requireAuth: true },
    { title: t('nav.referral'), icon: <DollarOutlined style={{ color: theme.primary }} />, path: '/profile', requireAuth: true },
    { title: t('nav.profile'), icon: <UserOutlined />, path: '/profile', requireAuth: true },
    { title: t('nav.help'), icon: <QuestionCircleOutlined />, path: '/help', requireAuth: false },
  ];

  const searchOverlay = showMobileSearch && typeof document !== 'undefined'
    ? createPortal(
      <div className="mobile-search-overlay">
        <div className="mobile-search-header">
          <Button size="middle" className="search-modal-back" type="text" icon={<ArrowLeftOutlined />} onClick={closeSearchModal} />
          <Input
            autoFocus
            placeholder={t('common.searchGames')}
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            onPressEnter={onGlobalSearch}
            className="search-modal-input"
            style={fontStyle}
          />
        </div>
        <div className="mobile-search-body">
          <div className="mobile-search-content">
            <SearchResultList results={filteredResults} onSelect={handleSelectGame} fontStyle={fontStyle} />
          </div>
        </div>
      </div>,
      document.body
    )
    : null;

  return (
    <>
    {searchOverlay}
    <Header ref={headerRef} className={`store-navbar${autoHideHeader ? ' store-navbar--scroll-aware' : ''}${headerHidden ? ' store-navbar--scroll-hidden' : ''}`} style={{
      background: scrolled ? 'rgb(var(--color-background-soft-rgb) / 0.92)' : theme.background,
      backdropFilter: scrolled ? 'blur(12px)' : 'none',
      padding: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: scrolled && !headerHidden ? 'var(--shadow-sm)' : 'none',
      borderBottom: '1px solid rgb(var(--color-border-rgb) / 0.85)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      width: '100%',
      zIndex: 1000,
      height: 'var(--store-header-height)',
      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), background 0.3s ease, box-shadow 0.3s ease',
      ...fontStyle
    }}>
      <div className="store-navbar-inner">
      {/* --- BRANDING --- */}
      <div onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
        <div className="logo-container">
          <img src="/image/jla-logo.webp" alt="JLASTORE" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
        <span className="brand-text">JLASTORE</span>
      </div>

      {/* --- SEARCHBAR (DESKTOP) --- */}
      <div className="nav-search-desktop" ref={desktopSearchRef}>
        <Input
          prefix={<SearchOutlined style={{ color: theme.muted }} />}
          suffix={searchValue ? <CloseOutlined onClick={() => handleSearchChange('')} style={{ cursor: 'pointer', fontSize: 12 }} /> : null}
          placeholder={t('common.searchGames')}
          value={searchValue}
          onChange={(e) => handleSearchChange(e.target.value)}
          onPressEnter={onGlobalSearch}
          onFocus={openSearchModal}
          onClick={openSearchModal}
          className="search-input store-input"
          style={fontStyle}
        />
        {showDesktopSearch && (
          <div className="desktop-search-panel">
            <SearchResultList results={filteredResults.slice(0, 10)} onSelect={handleSelectGame} fontStyle={fontStyle} />
          </div>
        )}
      </div>

      {/* --- ACTIONS --- */}
      <div className="nav-actions-container">
        <div className="desktop-actions">
          <Button size="middle"
            type="default"
            className="preference-trigger"
            onClick={openPreferencesModal}
            aria-label={t('nav.languageCurrency')}
          >
            {preferenceLabel}
          </Button>
          {isLoggedIn ? (
            <>
              <Button size="middle"
                className="wallet-button"
                icon={<WalletOutlined />}
                onClick={() => navigate('/wallet')}
                style={fontStyle}
              >
                <div className="wallet-info">
                  <span className="wallet-value" style={fontStyle}>
                    ${parseFloat(userInfo?.balance || 0).toFixed(2)}
                  </span>
                </div>
              </Button>

              <Avatar
                size={44}
                src={buildAvatarSrc(userInfo)}
                style={{ border: `2px solid ${theme.primary}`, cursor: 'pointer', background: theme.primarySoft }}
                onClick={() => navigate('/profile')}
              />
            </>
          ) : (
            <>
              <Button size="middle"
                type="text"
                onClick={() => navigate('/login')}
                style={{ fontWeight: 600, ...fontStyle }}
              >
                {t('nav.login')}
              </Button>
              <Button size="middle"
                type="primary"
                shape="round"
                onClick={() => navigate('/register')}
                style={{ fontWeight: 600, background: theme.primary, color: 'var(--color-on-primary)', ...fontStyle }}
              >
                {t('nav.signUp')}
              </Button>
            </>
          )}
        </div>

        <div className="universal-icons">
          {isLoggedIn ? (
            <>
              {/* Mobile Wallet Indicator (Hidden on Desktop) */}
              <button
                type="button"
                className="mobile-wallet-btn"
                aria-label={t('nav.wallet')}
                onClick={() => navigate('/wallet')}
              >
                <WalletOutlined style={{ color: theme.primary, fontSize: 'var(--store-button-icon-size)' }} />
                <span style={{ fontWeight: 700, color: theme.success, fontSize: 'var(--store-button-font-size)' }}>
                  ${parseFloat(userInfo?.balance || 0).toFixed(2)}
                </span>
              </button>

              <Tooltip title={isConnected ? t('nav.notificationsActive') : t('nav.setupNotifications')}>
                  <Badge dot={!isConnected} size="small" offset={[-4, 4]}>
                    <Button size="middle"
                      type="text"
                      shape="circle"
                      aria-label={isConnected ? t('nav.notificationsActive') : t('nav.setupNotifications')}
                      icon={<BellOutlined style={{ fontSize: 'var(--store-button-icon-size)', color: isConnected ? theme.success : theme.muted }} />}
                      onClick={() => {
                          setIsTelegramModalOpen(true);
                          if(!isConnected) setGeneratedOtp(null);
                      }}
                    />
                  </Badge>
              </Tooltip>
            </>
          ) : (
            // Mobile Sign In Button (Hidden on Desktop)
            <div className="mobile-login-btn">
              <Button size="middle"
                type="primary"
                shape="round"
                className="glass-sign-in-btn"
                onClick={() => navigate('/login')}
                style={{ fontWeight: 600, ...fontStyle }}
              >
                {t('nav.signIn')}
              </Button>
            </div>
          )}

        <div className="mobile-search-trigger">
            <Button size="middle" type="text" aria-label={t('common.searchGames')} icon={<SearchOutlined style={{ fontSize: 'var(--store-button-icon-size)' }} />} onClick={openSearchModal} />
        </div>
          <Button size="middle" type="text" icon={<MenuOutlined style={{ fontSize: 'var(--store-button-icon-size)', color: theme.foreground }} />} onClick={() => setMobileOpen(true)} />
        </div>
      </div>

      </div>
      {/* --- TELEGRAM MODAL --- */}
      <DeferredNavbarDialog
        loadDialog={loadTelegramDialog}
        open={isTelegramModalOpen}
        onCancel={() => { setIsTelegramModalOpen(false); setGeneratedOtp(null); }}
        width={420}
        className="telegram-modal"
        isConnected={isConnected} theme={theme} fontStyle={fontStyle} telegramIconSrc={telegramIconSrc}
        generatedOtp={generatedOtp} botUsername={botUsername} actionLoading={actionLoading}
        otpTextRef={otpTextRef} selectOtpText={selectOtpText} handleCopyCode={handleCopyCode}
        handleTestMessage={handleTestMessage} handleDisconnect={handleDisconnect} handleGenerateOtp={handleGenerateOtp}
      />

      {/* --- LANGUAGE & CURRENCY MODAL --- */}
      <DeferredNavbarDialog
        loadDialog={loadPreferencesDialog}
        open={isPreferencesModalOpen}
        onCancel={() => setIsPreferencesModalOpen(false)}
        title={<span style={{ fontWeight: 800, ...fontStyle }}>{t('nav.languageCurrency')}</span>}
        width={400}
        className="preferences-modal"
        fontStyle={fontStyle} draftLanguage={draftLanguage} setDraftLanguage={setDraftLanguage}
        draftPaymentCurrency={draftPaymentCurrency} setDraftPaymentCurrency={setDraftPaymentCurrency}
        availableCurrencies={availableCurrencies} confirmPreferences={confirmPreferences}
      />

      {/* --- DRAWER (SIDEBAR) --- */}
      <Drawer
        title={
          isLoggedIn ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, ...fontStyle }} onClick={() => { navigate('/profile'); setMobileOpen(false); }}>
              <Avatar size="large" src={buildAvatarSrc(userInfo)} />
              <div style={fontStyle}>
                <div style={{ fontSize: 16, fontWeight: 700, ...fontStyle }}>{userInfo?.username || t('nav.userFallback')}</div>
                <div style={{ fontSize: 13, color: theme.success, fontWeight: 600, ...fontStyle }}>
                  ${parseFloat(userInfo?.balance || 0).toFixed(2)}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, ...fontStyle }}>
              <Avatar size="large" icon={<UserOutlined />} />
              <div style={fontStyle}>
                <div style={{ fontSize: 16, fontWeight: 700, ...fontStyle }}>{t('nav.welcomeGuest')}</div>
                <div style={{ fontSize: 12, color: theme.muted, ...fontStyle }} onClick={() => navigate('/login')}>
                  {t('nav.tapToLogin')}
                </div>
              </div>
            </div>
          )
        }
        placement="right"
        onClose={() => setMobileOpen(false)}
        open={mobileOpen}
        size={280}
        styles={{ body: { display: 'flex', flexDirection: 'column' } }}
      >
        <button
          type="button"
          className="store-button preference-trigger drawer-preference-trigger"
          onClick={() => {
            setMobileOpen(false);
            openPreferencesModal();
          }}
          aria-label={t('nav.languageCurrency')}
        >
          {preferenceLabel}
        </button>
        <div className="drawer-menu-list">
          {finalMenuItems.map((item, index) => {
            if (item.requireAuth && !isLoggedIn) return null;

            return (
              <button
                key={`${item.path}-${index}`}
                type="button"
                onClick={() => { navigate(item.path); setMobileOpen(false); }}
                className="drawer-menu-item"
                style={fontStyle}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 16, ...fontStyle }}>
                  <span style={{ color: theme.primary, display: 'flex' }}>{item.icon}</span>
                  <span style={{ fontWeight: 600, ...fontStyle }}>{item.title}</span>
                </div>
              </button>
            );
          })}
        </div>
        <div className="drawer-footer">
          {isLoggedIn ? (
            <Button block danger icon={<LogoutOutlined />} type="primary" shape="round" size="middle" style={fontStyle} onClick={handleLogout}>
              {t('nav.logout')}
            </Button>
          ) : (
            <Button block type="primary" icon={<LoginOutlined />} shape="round" size="middle" style={{ background: theme.primary, color: 'var(--color-on-primary)', ...fontStyle }} onClick={() => { navigate('/login'); setMobileOpen(false); }}>
              {t('nav.loginRegister')}
            </Button>
          )}
        </div>
      </Drawer>

      <style>{`
        .store-navbar--scroll-hidden { transform: translateY(-100%); pointer-events: none; }
        .store-navbar--scroll-hidden:has(:focus-visible) { transform: translateY(0); pointer-events: auto; }
        @media (prefers-reduced-motion: reduce) {
          .store-navbar--scroll-aware { transition: none !important; }
        }
        .store-navbar-inner { display: flex; align-items: center; justify-content: space-between; gap: 24px; width: 100%; max-width: var(--store-content-width, 1280px); min-width: 0; height: 100%; margin: 0 auto; padding: 0 var(--store-page-padding); }
        .store-navbar-inner > :first-child { flex-shrink: 0; }
.logo-container {
  width: 44px;
  height: 44px;

  display: flex;
  align-items: center;
  justify-content: center;
}
        .brand-text {
          font-size: 22px; font-weight: 900;
          color: var(--color-foreground); letter-spacing: -0.5px;
        }
        .nav-search-desktop {
          flex: 1; min-width: 0; max-width: 450px; margin: 0; position: relative;
        }
        .search-input {
          border-radius: 8px; background: #ffffff; height: var(--store-input-height); min-height: var(--store-input-height); border: 1px solid #e5e7eb; font-size: var(--store-input-font-size);
        }
        .desktop-search-panel {
          position: absolute;
          top: calc(100% + 10px);
          left: 0;
          right: 0;
          z-index: 1200;
          max-height: min(520px, calc(100vh - 110px));
          overflow-y: auto;
          padding: 10px;
          background: #ffffff;
          border: 1px solid var(--color-border-soft);
          border-radius: var(--store-card-radius);
          box-shadow: none;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .desktop-search-panel::-webkit-scrollbar {
          width: 0;
          height: 0;
          display: none;
        }
        .desktop-search-panel .search-results-list {
          gap: 2px;
        }
        .desktop-search-panel .search-result-item {
          min-height: 58px;
          gap: 12px;
          padding: 7px 8px;
          border-radius: 12px;
        }
        .desktop-search-panel .search-result-item:hover {
          background: var(--color-primary-soft);
          transform: none;
        }
        .desktop-search-panel .search-result-image {
          width: 42px;
          height: 42px;
          border-radius: 9px;
        }
        .desktop-search-panel .search-result-title {
          font-size: 14px;
          margin-bottom: 2px;
        }
        .desktop-search-panel .search-result-message {
          font-size: 12px;
          min-height: 14px;
        }
        .desktop-search-panel .search-result-status {
          min-width: 70px;
          gap: 5px;
          font-size: 12px;
        }
        .desktop-search-panel .search-result-status::before {
          width: 6px;
          height: 6px;
        }
        .desktop-search-panel .search-empty-state {
          min-height: 180px;
          padding-top: 36px;
        }
        .desktop-search-panel .search-empty-title {
          font-size: 14px;
          margin-bottom: 10px;
        }
        .desktop-search-panel .search-empty-feedback {
          font-size: var(--store-button-font-size);
        }
        .search-result-item:hover { background: var(--color-primary-soft); }
        .nav-actions-container {
          display: flex; align-items: center; gap: 20px;
        }
        .desktop-actions {
          display: flex; align-items: center; gap: 20px;
        }
        .wallet-button {
          border-radius: var(--radius-md); height: var(--store-button-height); padding: var(--store-button-padding-block) var(--store-button-padding-inline);
          background: transparent !important;
          border: none !important;
          display: flex; align-items: center; gap: var(--store-button-gap);
          box-shadow: none !important;
        }
        .wallet-button:hover,
        .wallet-button:focus,
        .wallet-button:active {
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
        }
        .wallet-info { text-align: right; line-height: var(--store-button-line-height); }
        .wallet-value { font-size: var(--store-button-font-size); font-weight: 800; color: var(--color-success); }

        .universal-icons { display: flex; align-items: center; gap: 10px; }
        .mobile-search-trigger { display: none; }
        .mobile-search-overlay {
          position: fixed; inset: 0; height: 100dvh; background: var(--color-background); z-index: 3000;
          display: flex; flex-direction: column; animation: fadeIn 0.16s ease-out;
        }
        .mobile-search-header {
          display: flex; align-items: center; gap: 8px; padding: 5px var(--store-page-padding);
          border-bottom: 1px solid var(--color-border-soft); min-height: var(--store-header-height);
          width: 100%; max-width: none; margin: 0;
        }
        .search-modal-back {
          padding: 0;
          min-width: var(--store-button-height);
          width: var(--store-button-height);
          height: var(--store-button-height);
          color: var(--color-foreground);
          font-size: var(--store-button-icon-size);
        }
        .search-modal-input {
          flex: 1;
          height: var(--store-input-height); min-width: 0;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          background: #ffffff;
          box-shadow: none;
          font-size: var(--store-input-font-size);
          font-weight: 500;
        }
        .search-modal-input:hover,
        .search-modal-input:focus,
        .search-modal-input.ant-input-affix-wrapper-focused {
          border-color: var(--color-primary) !important;
          box-shadow: 0 0 0 2px rgb(var(--color-primary-rgb) / 0.14) !important;
        }
        .search-modal-input .ant-input {
          font-size: var(--store-input-font-size);
          line-height: 1.2;
        }
        .search-modal-close {
          padding: 0;
          min-width: var(--store-button-height);
          width: var(--store-button-height);
          height: var(--store-button-height);
          color: #111827;
          font-size: var(--store-button-icon-size);
        }
        .mobile-search-body {
          flex: 1; min-height: 0;
          overflow-y: auto;
          background: #ffffff;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .mobile-search-body::-webkit-scrollbar {
          width: 0;
          height: 0;
          display: none;
        }
        .mobile-search-content {
          width: 100%; max-width: none; margin: 0; padding: 16px var(--store-page-padding) 32px;
        }
        .search-results-list {
          display: flex;
          flex-direction: column;
          gap: var(--store-card-gap);
        }
        .search-result-item {
          position: relative;
          display: flex;
          align-items: center;
          gap: 12px;
          min-height: 56px;
          cursor: pointer;
          transition: background 0.18s ease, transform 0.18s ease;
        }
        .search-result-item:hover {
          background: transparent;
          transform: translateY(-1px);
        }
        .search-result-image {
          width: 50px;
          height: 50px;
          border-radius: 10px;
          object-fit: cover;
          background: var(--color-background-soft);
          flex-shrink: 0;
        }
        .search-result-main {
          min-width: 0;
          flex: 1;
          padding-right: 12px;
        }
        .search-result-title {
          color: #020617;
          font-size: 14px;
          font-weight: 600;
          line-height: 1.6;
          margin: 0 0 3px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .search-result-message {
          color: var(--color-primary-deep);
          font-size: 14px;
          font-weight: 700;
          line-height: 1.2;
          min-height: 16px;
        }
        .search-result-status {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          min-width: 76px;
          justify-content: center;
          font-size: 13px;
          font-weight: 800;
          line-height: 1;
          flex-shrink: 0;
        }
        .search-result-status::before {
          content: "";
          width: 7px;
          height: 7px;
          border-radius: 999px;
          background: currentColor;
        }
        .search-result-status.is-active {
          color: #58b947;
        }
        .search-result-status.is-sold-out {
          color: #ef5350;
        }
        .search-empty-state {
          min-height: calc(100vh - 180px);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          padding-top: 110px;
          text-align: center;
          color: #9ca3af;
        }
        .search-empty-state .ant-empty {
          margin-bottom: 10px;
        }
        .search-empty-title {
          color: #9ca3af;
          font-size: 16px;
          line-height: 1.2;
          margin-bottom: 14px;
        }
        .search-empty-feedback {
          display: inline-flex;
          align-items: center;
          gap: var(--store-button-gap);
          border: none;
          background: transparent;
          color: var(--color-primary);
          font-size: var(--store-button-font-size);
          cursor: pointer;
          height: var(--store-button-height); padding: var(--store-button-padding-block) var(--store-button-padding-inline); line-height: var(--store-button-line-height);
        }
        .drawer-menu-list {
          display: flex;
          flex-direction: column;
        }
        .drawer-menu-item {
          cursor: pointer;
          border: none;
          padding: 16px 12px;
          border-radius: var(--radius-md);
          margin-bottom: 4px;
          transition: 0.2s;
          background: transparent;
          text-align: left;
          width: 100%;
        }
        .drawer-menu-item:hover { background: var(--color-primary-soft); }
        .preference-trigger {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 82px;
          height: var(--store-button-height);
          padding: var(--store-button-padding-block) var(--store-button-padding-inline);
          border: 1px solid var(--color-border);
          border-radius: 8px;
          background: #ffffff;
          color: var(--color-foreground);
          font-size: var(--store-button-font-size);
          font-weight: 800;
          line-height: var(--store-button-line-height); gap: var(--store-button-gap);
          cursor: pointer;
          box-shadow: none !important;
          transition: transform 0.2s ease, background 0.2s ease;
        }
        .preference-trigger:hover {
          background: var(--color-primary-soft);
          border-color: var(--color-primary);
          color: var(--color-primary-deep);
          box-shadow: none !important;
        }
        .preference-trigger:focus,
        .preference-trigger:active {
          box-shadow: none !important;
        }
        .drawer-preference-trigger {
          width: 100%;
          margin-bottom: 18px;
          border-radius: 8px;
        }
        .preferences-modal .ant-modal-content {
          border-radius: var(--store-card-radius); border: 1px solid #e5e7eb; background: #ffffff; box-shadow: none;
          padding: 20px;
        }
        .preferences-modal .ant-modal-header {
          margin-bottom: 18px;
        }
        .preferences-modal .ant-modal-title {
          font-size: var(--store-section-title-size);
        }
        .preferences-form {
          display: flex;
          flex-direction: column;
        }
        .preferences-label {
          margin-bottom: 8px;
          color: #5f6368;
          font-size: 14px;
          font-weight: 500;
        }
        .preferences-select {
          width: 100%;
          margin-bottom: 16px;
        }
        .preferences-select .ant-select-selector {
          min-height: var(--store-input-height) !important; height: var(--store-input-height);
          padding: 0 12px !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 8px !important;
          background: #ffffff !important;
          box-shadow: none !important;
        }
        .preferences-select:hover .ant-select-selector,
        .preferences-select.ant-select-focused .ant-select-selector {
          border-color: rgb(var(--color-primary-rgb) / 0.55) !important;
          box-shadow: 0 0 0 3px rgb(var(--color-primary-rgb) / 0.12) !important;
        }
        .preferences-select .ant-select-selection-item {
          display: flex;
          align-items: center;
          color: #171717;
          font-size: var(--store-input-font-size);
        }
        .preferences-select-popup {
          padding: 6px !important;
          border: 1px solid var(--color-border-soft);
          border-radius: 10px !important;
          box-shadow: var(--shadow-lg) !important;
        }
        .preferences-select-popup .ant-select-item {
          min-height: 40px;
          display: flex;
          align-items: center;
          border-radius: 7px;
          font-size: 15px;
        }
        .preferences-select-popup .ant-select-item-option-selected {
          background: rgb(var(--color-primary-rgb) / 0.12) !important;
          color: var(--color-primary-deep);
          font-weight: 700;
        }
        .preferences-select-popup .ant-select-item-option-active:not(.ant-select-item-option-selected) {
          background: rgb(var(--color-primary-rgb) / 0.06) !important;
        }
        .preferences-confirm.ant-btn {
          height: var(--store-button-height); padding: var(--store-button-padding-block) var(--store-button-padding-inline); line-height: var(--store-button-line-height); gap: var(--store-button-gap);
          margin-top: 4px;
          border: none !important;
          border-radius: 8px !important;
          background: var(--color-primary) !important;
          color: var(--color-on-primary) !important;
          font-size: var(--store-button-font-size);
          font-weight: 800;
          box-shadow: none !important;
        }
        .preferences-confirm.ant-btn:hover,
        .preferences-confirm.ant-btn:focus {
          background: var(--color-primary-strong) !important;
          color: #ffffff !important;
        }
        .drawer-footer { margin-top: auto; padding-top: 24px; }

        /* Telegram Modal Styles */
        .telegram-modal .ant-modal-content {
          border-radius: var(--radius-xl);
          background: #ffffff;
          border: 1px solid #e5e7eb;
          box-shadow: none;
        }

        /* Mobile Wallet & Mobile Login */
        .mobile-wallet-btn, .mobile-login-btn {
          display: none;
        }

        @property --signin-border-angle {
          syntax: '<angle>';
          inherits: false;
          initial-value: 0deg;
        }

        .glass-sign-in-btn.ant-btn {
          --signin-border-angle: 0deg;
          position: relative;
          overflow: hidden;
          height: var(--store-button-height);
          padding: var(--store-button-padding-block) var(--store-button-padding-inline); font-size: var(--store-button-font-size); line-height: var(--store-button-line-height); gap: var(--store-button-gap);
          border: 1.5px solid transparent !important;
          color: var(--color-on-primary) !important;
          background:
            linear-gradient(
              135deg,
              rgb(var(--color-primary-rgb) / 0.82) 0%,
              rgb(var(--color-primary-strong-rgb) / 0.72) 55%,
              rgb(255 255 255 / 0.32) 100%
            ) padding-box,
            conic-gradient(
              from var(--signin-border-angle),
              rgb(255 255 255 / 0.72),
              rgb(var(--color-primary-rgb) / 0.72),
              rgb(255 255 255 / 0.92),
              rgb(var(--color-primary-rgb) / 0.72),
              rgb(255 255 255 / 0.72)
            ) border-box !important;
          backdrop-filter: blur(14px) saturate(1.25);
          -webkit-backdrop-filter: blur(14px) saturate(1.25);
          box-shadow:
            0 10px 24px rgb(var(--color-primary-rgb) / 0.20),
            0 0 0 3px rgb(var(--color-primary-rgb) / 0.055),
            inset 0 1px 0 rgb(255 255 255 / 0.62);
          animation:
            glassSignInBorderSpin 2.8s linear infinite,
            glassSignInGlow 2.2s ease-in-out infinite !important;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .glass-sign-in-btn.ant-btn::before {
          content: "";
          position: absolute;
          inset: -38%;
          z-index: 0;
          pointer-events: none;
          border-radius: inherit;
          opacity: 0.7;
          background:
            radial-gradient(circle at 22% 20%, rgb(255 255 255 / 0.86), transparent 32%),
            radial-gradient(circle at 74% 78%, rgb(var(--color-primary-rgb) / 0.28), transparent 38%);
          filter: blur(12px);
          animation: glassSignInColorFloat 3.4s ease-in-out infinite alternate;
        }

        .glass-sign-in-btn.ant-btn::after {
          content: "";
          position: absolute;
          inset: 0;
          z-index: 1;
          pointer-events: none;
          border-radius: inherit;
          opacity: 0.5;
          background:
            linear-gradient(
              105deg,
              transparent 0%,
              transparent 28%,
              rgb(255 255 255 / 0.65) 46%,
              transparent 64%,
              transparent 100%
            );
          background-size: 230% 100%;
          animation: glassSignInShine 2.6s ease-in-out infinite;
        }

        .glass-sign-in-btn.ant-btn > span {
          position: relative;
          z-index: 2;
          text-shadow: 0 1px 8px rgb(0 0 0 / 0.12);
        }

        .glass-sign-in-btn.ant-btn:hover,
        .glass-sign-in-btn.ant-btn:focus {
          color: var(--color-on-primary) !important;
          transform: translateY(-1px);
          box-shadow:
            0 14px 30px rgb(var(--color-primary-rgb) / 0.25),
            0 0 0 3px rgb(var(--color-primary-rgb) / 0.08),
            inset 0 1px 0 rgb(255 255 255 / 0.72);
        }

        @keyframes glassSignInBorderSpin {
          to { --signin-border-angle: 360deg; }
        }

        @keyframes glassSignInGlow {
          0%, 100% { filter: saturate(1) brightness(1); }
          50% { filter: saturate(1.12) brightness(1.04); }
        }

        @keyframes glassSignInColorFloat {
          from { transform: translate3d(-4%, -5%, 0) scale(1); }
          to { transform: translate3d(5%, 4%, 0) scale(1.08); }
        }

        @keyframes glassSignInShine {
          0% { background-position: 135% 0; }
          58%, 100% { background-position: -135% 0; }
        }

        @keyframes slideIn { from { transform: translateY(-10px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

        @media (max-width: 1024px) {
          .brand-text { font-size: 18px; }
          .store-navbar-inner { gap: 16px; }
        }
        @media (max-width: 850px) {
          .brand-text { display: none; }
          .nav-search-desktop { display: none !important; }
          .desktop-actions { display: none !important; }
          .mobile-search-trigger { display: block !important; }

          /* Show on mobile cleanly */
          .mobile-wallet-btn {
            max-width: 100px;
            min-width: 0;
            display: flex;
            align-items: center;
            gap: var(--store-button-gap);
            min-height: var(--store-button-height);
            height: var(--store-button-height);
            font-size: var(--store-button-font-size);
            line-height: var(--store-button-line-height);
            background: transparent;
            padding: var(--store-button-padding-block) 0;
            cursor: pointer;
            border: none;
            margin-right: 5px;
          }

          .mobile-wallet-btn > span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

          .mobile-login-btn {
            display: block;
            margin-right: 4px;
          }
        }
        @media (max-width: 480px) {
          .brand-text { display: none; }
          .store-navbar-inner { gap: 12px; }
          .logo-container { width: 36px; height: 36px; }
        }
        .telegram-modal h3.ant-typography { font-size: var(--store-section-title-size); }
        @media (max-width: 767px) {
          .universal-icons { gap: 8px; }
          .nav-actions-container { gap: 12px; min-width: 0; }
          .preferences-modal .ant-modal-content { padding: 16px; }
          .search-result-main { padding-right: 0; }
          .search-result-message { overflow-wrap: anywhere; line-height: 1.5; }
          .preferences-confirm.ant-btn { width: 100%; height: auto; min-height: var(--store-button-height); }
        }
      `}</style>
    </Header>
    </>
  );
};

const SearchResultList = ({ results, onSelect, fontStyle }) => {
  const { t } = useI18n();
  const navigate = useNavigate();

  if (results.length === 0) {
    return (
      <div className="search-empty-state" style={fontStyle}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={false} />
        <div className="search-empty-title">
          {t('nav.searchCannotFind', 'Cannot find the item')}
        </div>
        <button
          type="button"
          className="store-button search-empty-feedback"
          onClick={() => navigate('/help')}
          style={fontStyle}
        >
          {t('nav.searchFeedback', 'Submit Game Feedback')}
          <RightOutlined style={{ fontSize: 'var(--store-button-icon-size)' }} />
        </button>
      </div>
    );
  }

  return (
    <div className="search-results-list">
      {results.map((game) => {
        const isActive = Number(game.stock_status) === 1;
        const statusText = isActive ? t('common.active') : t('common.soldOut');

        return (
        <div
          key={game.id}
          onClick={() => onSelect(game.id)}
          className="search-result-item"
          style={fontStyle}
        >
          <img
            src={getSearchImageSrc(game)}
            alt={game.game_name || game.title || ''}
            className="search-result-image"
          />
          <div className="search-result-main">
            <div className="search-result-title" style={fontStyle}>
              {game.game_name || game.title || t('common.noData')}
            </div>
            <div className="search-result-message" style={fontStyle}>
              {game.message || game.title || ''}
            </div>
          </div>
          <span className={`search-result-status ${isActive ? 'is-active' : 'is-sold-out'}`} style={fontStyle}>
            {statusText}
          </span>
        </div>
        );
      })}
    </div>
  );
};

export default Navbar;
